import React, {useEffect, useState} from "react";
import {observer} from "mobx-react-lite";
import {useNavigate, useParams} from "react-router-dom";

import {FileUpload} from "primereact/fileupload";

import clubStore from "../../../storage/ClubStore";
import appStore from "../../../storage/AppStore";
import systemStore from "../../../storage/SystemStore";

import Input from "../../../ui/Input";
import Select from "../../../ui/Select";
import Btn from "../../../ui/Btn";

import {getBaseUrl} from "../../../common/config";

import "../../../common/game.css";
import "../../../common/billing.css";


const BillingDetail = () => {
    const {id} = useParams();
    const navigate = useNavigate();

    const [savingCheque, setSavingCheque] = useState(false);
    const [chequeUploadKey, setChequeUploadKey] = useState(0);

    const bill = clubStore.billing_detail || {};
    const group = bill.billing_group || {};

    const cheques = bill.cheques || [];

    const pdfCheques = cheques.filter(
        (ch) => ch.mimetype === "application/pdf"
    );

    const imageCheques = cheques.filter(
        (ch) => ch.mimetype !== "application/pdf"
    );


    useEffect(() => {
        clubStore.getBillingDetail(id);
    }, [id]);


    const paymentOptions = [
        {
            id: "cash",
            name: appStore.tr.cash
        },
        {
            id: "transfer",
            name: appStore.tr.transfer
        }
    ];


    const canEdit =
        !bill.is_paid ||
        !bill.is_approved;


    const canPay =
        !bill.is_paid &&
        !!bill.payment_method &&
        (
            bill.payment_method !== "transfer" ||
            !!bill.cheque_upload
        );


    const handleChangeAmount = async () => {
        if (!bill.amount) {
            return;
        }

        try {
            await clubStore.billing_detail.changeAmount();

            systemStore.setSuccess(
                appStore.tr.successfully_saved
            );
        } catch (error) {
            systemStore.setError(
                error.message
            );
        }
    };


    const handlePay = async () => {
        try {
            await clubStore.billing_detail.pay();

            systemStore.setSuccess(
                appStore.tr.successfully_saved
            );
        } catch (error) {
            systemStore.setError(
                appStore.tr[error] ||
                error.message ||
                error
            );
        }
    };


    // Upload another receipt to an already paid transfer bill.
    // Existing pay() flow appends cheque_upload to the bill's cheques.
    const handleAddCheque = async () => {
        if (!bill.is_paid || bill.payment_method !== "transfer" || !bill.cheque_upload || savingCheque) return;

        setSavingCheque(true);
        try {
            await clubStore.billing_detail.pay();
            clubStore.billing_detail.setProperty("cheque_upload", null);
            setChequeUploadKey((prev) => prev + 1);
            await clubStore.getBillingDetail(id);
            systemStore.setSuccess(appStore.tr.successfully_saved);
        } catch (error) {
            systemStore.setError(appStore.tr[error] || error.message || error);
        } finally {
            setSavingCheque(false);
        }
    };


    const handleSaveNote = async () => {
        try {
            await clubStore.billing_detail.saveNote();

            systemStore.setSuccess(
                appStore.tr.successfully_saved
            );
        } catch (error) {
            systemStore.setError(
                error.message
            );
        }
    };


    const handleApprove = async () => {
        await clubStore.billing_detail.approve();

        systemStore.setSuccess(
            appStore.tr.successfully_saved
        );
    };


    const handleDecline = async () => {
        await clubStore.billing_detail.decline();

        systemStore.setSuccess(
            appStore.tr.successfully_saved
        );
    };


    return (
        <div className="billing-page grid">

            {/* HEADER */}

            <div className="col-12">

                <div className="game-card billing-header-card">

                    <div className="billing-header-main">

                        <div className="flex align-items-center gap-2 justify-content-between">
                            <div>

                                <div className="billing-header-title">
                                    {appStore.tr.bill_no}: {bill.id}
                                    {bill.company_user?.title &&
                                        `, ${bill.company_user.title}`
                                    }
                                </div>


                                <div className="billing-header-subtitle">

                                    <div className="text-600">
                                        {group.billing_type?.name}
                                    </div>

                                    <div className="billing-object-title">
                                        {group.name}
                                    </div>

                                </div>

                            </div>
                            <Btn
                                icon="pi pi-arrow-left"
                                severity="secondary"
                                outlined
                                onClick={() => {
                                    if (group.id) {
                                        navigate(
                                            `/manager/billing/group/${group.id}`
                                        );
                                    } else {
                                        navigate(
                                            "/manager/billing/info"
                                        );
                                    }
                                }}
                            />
                        </div>

                    </div>


                    <div className="game-badges billing-status-badges">

                        {!bill.is_paid && (
                            <span className="badge badge-draft">
                                {appStore.tr.unpaid}
                            </span>
                        )}


                        {bill.is_paid && (
                            <span className="badge badge-finished">
                                {appStore.tr.paid}
                            </span>
                        )}


                        {bill.is_paid &&
                            !bill.is_approved && (
                            <span className="badge badge-draft">
                                {appStore.tr.not_approved}
                            </span>
                        )}


                        {bill.is_paid &&
                            bill.is_approved && (
                            <span className="badge badge-upcoming">
                                {appStore.tr.approved}
                            </span>
                        )}


                        {!!bill.amount && (
                            <span className="badge badge-fee">
                                {bill.amount}
                                {bill.currency?.sign || ""}
                            </span>
                        )}


                        {bill.payment_method && (
                            <span className="badge badge-tournament">
                                {
                                    bill.payment_method === "cash"
                                        ? appStore.tr.cash
                                        : appStore.tr.transfer
                                }
                            </span>
                        )}

                    </div>

                </div>

            </div>


            {/* GROUP */}

            <div className="col-12">

                <div className="game-card">

                    <div className="grid">

                        <div className="col-12 md:col-4">

                            <div className="text-600">
                                {appStore.tr.billing_type}
                            </div>

                            <strong>
                                {group.billing_type?.name}
                            </strong>

                        </div>


                        {group.object?.name && (
                            <div className="col-12 md:col-4">

                                <div className="text-600">
                                    {appStore.tr.payment_purpose || "Объект"}
                                </div>

                                <strong>
                                    {group.object.name}
                                </strong>

                            </div>
                        )}


                        <div className="col-12 md:col-4">

                            <div className="text-600">
                                {appStore.tr.period || "Период"}
                            </div>

                            <strong>
                                {group.period_from}

                                {group.period_to &&
                                group.period_to !== group.period_from
                                    ? ` — ${group.period_to}`
                                    : ""
                                }
                            </strong>

                        </div>

                    </div>

                </div>

            </div>


            {/* TRAINING STATS */}

            {bill.trainings?.total > 0 && (

                <div className="col-12">

                    <div className="game-card billing-trainings-card">

                        <div className="competition-section-title">
                            {appStore.tr.training_stats ||
                                "Статистика тренировок"}
                        </div>


                        <div className="billing-trainings-text">

                            {bill.trainings.participated}
                            /
                            {bill.trainings.total}

                            {" ("}

                            {bill.trainings.calculated}
                            /
                            {bill.trainings.fee}

                            {bill.currency?.sign || ""}

                            {")"}

                        </div>

                    </div>

                </div>

            )}


            {/* AMOUNT */}

            <div className="col-12 lg:col-4">

                <div className="game-page billing-block-card billing-block-stretch">

                    <div className="competition-section-title">
                        {appStore.tr.change_amount}
                    </div>


                    <div className="billing-block-body">

                        <div className="billing-inline-row">

                            {bill.trainings?.total > 0 && (

                                <Btn
                                    icon="pi pi-sync"
                                    severity="info"
                                    tooltip={appStore.tr.calculate}
                                    disabled={!canEdit}
                                    onClick={() => {
                                        clubStore.billing_detail.setProperty(
                                            "amount",
                                            bill.trainings.calculated
                                        );
                                    }}
                                />

                            )}


                            <div className="billing-flex-1">

                                <Input
                                    type="number"
                                    id="amount"
                                    store={clubStore.billing_detail}
                                    prop="amount"
                                    label={appStore.tr.amount}
                                    disabled={!canEdit}
                                    full
                                />

                            </div>

                        </div>


                        <Btn
                            icon="pi pi-save"
                            severity="success"
                            label={appStore.tr.save}
                            disabled={
                                !bill.amount ||
                                !canEdit
                            }
                            onClick={handleChangeAmount}
                        />

                    </div>

                </div>

            </div>


            {/* PAYMENT */}

            <div className="col-12 lg:col-4">

                <div className="game-page billing-block-card billing-block-stretch">

                    <div className="competition-section-title">
                        {appStore.tr.payment}
                    </div>


                    <div className="billing-block-body">

                        <Select
                            store={clubStore.billing_detail}
                            prop="payment_method"
                            label={appStore.tr.payment_method}
                            options={paymentOptions}
                            disabled={
                                bill.is_paid &&
                                bill.is_approved
                            }
                        />


                        {bill.payment_method === "transfer" && (

                            <div className="billing-upload-wrap">

                                <FileUpload
                                    key={chequeUploadKey}
                                    mode="basic"
                                    name="input_cheque[]"
                                    accept="image/*,application/pdf"
                                    chooseLabel={bill.is_paid ? (appStore.tr.add_cheque || "Добавить чек") : appStore.tr.cheque}
                                    customUpload
                                    disabled={savingCheque}
                                    onSelect={(event) => {
                                        clubStore.billing_detail.setProperty("cheque_upload", event.files[0] || null);
                                    }}
                                />

                                {bill.is_paid && (
                                    <Btn
                                        icon="pi pi-upload"
                                        severity="success"
                                        label={appStore.tr.save || "Сохранить"}
                                        disabled={!bill.cheque_upload || savingCheque}
                                        loading={savingCheque}
                                        onClick={handleAddCheque}
                                    />
                                )}

                            </div>

                        )}


                        {!bill.is_paid && (

                            <Btn
                                icon="pi pi-check"
                                severity="success"
                                label={appStore.tr.paid}
                                disabled={!canPay}
                                onClick={handlePay}
                            />

                        )}


                        {bill.is_paid &&
                            !bill.is_approved && (

                            <div className="billing-actions-row">

                                <Btn
                                    icon="pi pi-check-circle"
                                    severity="success"
                                    label={appStore.tr.approve}
                                    onClick={handleApprove}
                                />

                                <Btn
                                    icon="pi pi-times-circle"
                                    severity="danger"
                                    label={appStore.tr.decline}
                                    onClick={handleDecline}
                                />

                            </div>

                        )}

                    </div>

                </div>

            </div>


            {/* NOTES */}

            <div className="col-12 lg:col-4">

                <div className="game-page billing-block-card billing-block-stretch">

                    <div className="competition-section-title">
                        {appStore.tr.notes || "Notes"}
                    </div>


                    <div className="billing-block-body">

                        <Input
                            store={clubStore.billing_detail}
                            prop="notes"
                            label={appStore.tr.notes}
                            full
                        />


                        <Btn
                            icon="pi pi-save"
                            severity="warning"
                            label={appStore.tr.save}
                            onClick={handleSaveNote}
                        />

                    </div>

                </div>

            </div>


            {/* PDF CHEQUES */}

            {pdfCheques.length > 0 && (

                <div className="col-12">

                    <div className="game-card">

                        <div className="competition-section-title">
                            {appStore.tr.cheque}
                        </div>


                        <div className="billing-file-actions">

                            {pdfCheques.map(
                                (cheque, index) => (

                                    <a
                                        key={index}
                                        href={
                                            `${getBaseUrl()}/manager/billing/cheque/${bill.id}/${cheque.file_id}?jwt=${sessionStorage.getItem("jwt")}`
                                        }
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="no-underline"
                                    >

                                        <Btn
                                            label={
                                                cheque.filename ||
                                                appStore.tr.cheque
                                            }
                                            icon="pi pi-file-pdf"
                                            size="small"
                                        />

                                    </a>

                                )
                            )}

                        </div>

                    </div>

                </div>

            )}


            {/* IMAGE CHEQUES */}

            {imageCheques.length > 0 && (

                <div className="col-12">

                    <div className="game-card">

                        <div className="competition-section-title">
                            {appStore.tr.cheque}
                        </div>


                        <div className="billing-cheque-grid">

                            {imageCheques.map(
                                (cheque, index) => (

                                    <div
                                        key={index}
                                        className="billing-cheque-item"
                                    >

                                        <img
                                            src={
                                                `${getBaseUrl()}/manager/billing/cheque/${bill.id}/${cheque.file_id}?jwt=${sessionStorage.getItem("jwt")}`
                                            }
                                            alt="cheque"
                                            className="billing-cheque-img"
                                        />

                                    </div>

                                )
                            )}

                        </div>

                    </div>

                </div>

            )}

        </div>
    );
};


export default observer(BillingDetail);